import { useEffect, lazy, Suspense } from "react";
import axios from "axios";
import Navigation from "@/components/Navigation";
import HeroSection from "@/components/HeroSection";
import MusicPlayer from "@/components/MusicPlayer";
import IllustratorDrawer from "@/components/IllustratorDrawer";

// Lazy load below-the-fold components for faster initial page load
const AboutBook = lazy(() => import("@/components/AboutBook"));
const AboutAuthor = lazy(() => import("@/components/AboutAuthor"));
const YoutubeVideos = lazy(() => import("@/components/YoutubeVideos"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const ReflectionsMedia = lazy(() => import("@/components/ReflectionsMedia"));
const AboutPublisher = lazy(() => import("@/components/AboutPublisher"));
const ContactNewsletter = lazy(() => import("@/components/ContactNewsletter"));
const Footer = lazy(() => import("@/components/Footer"));

// Simple loading placeholder
const SectionLoader = () => (
  <div className="min-h-[200px] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
  </div>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  useEffect(() => {
    // Seed default data on first load
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (error) {
        // Seed endpoint may fail if already seeded - this is expected
        console.debug("Seed request completed:", error?.response?.status || "network error");
      }
    };
    seedData();
  }, []); // Empty deps is correct - only run once on mount

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Critical above-the-fold components - loaded immediately */}
      <Navigation />
      <MusicPlayer />
      <IllustratorDrawer />
      <HeroSection />
      
      {/* Below-the-fold components - lazy loaded */}
      <Suspense fallback={<SectionLoader />}>
        <AboutBook />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <AboutAuthor />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <YoutubeVideos />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <ReflectionsMedia />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <Testimonials />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <AboutPublisher />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <ContactNewsletter />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default HomePage;
